import React, { useEffect } from "react";
import TableComponent from "../../../../src/components/table/tableComponent";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchClassesThunk,
  fetchSubjectsByClassThunk,
  fetchChaptersThunk,
  createChapterThunk,
  updateChapterThunk,
  deleteChapterThunk,
} from "../redux/chapterThunk";
import {
  setSelectedClassId,
  setSelectedSubjectId,
  setChapterName,
  setEditingChapter,
} from "../redux/chapterSlice";

const ChapterManager = () => {
  const dispatch = useDispatch();
  const {
    classes,
    subjects,
    chapters,
    selectedClassId,
    selectedSubjectId,
    chapterName,
    editingChapter,
    loadingClasses,
    loadingSubjects,
    loadingChapters,
  } = useSelector((state) => state.chapter);



  useEffect(() => {
    dispatch(fetchClassesThunk());
  }, [dispatch]);

  useEffect(() => {
    if (selectedClassId) {
      dispatch(fetchSubjectsByClassThunk(selectedClassId));
    }
  }, [dispatch, selectedClassId]);

  useEffect(() => {
    if (selectedClassId && selectedSubjectId) {
      dispatch(fetchChaptersThunk({ classId: selectedClassId, subjectId: selectedSubjectId }));
    }
  }, [dispatch, selectedClassId, selectedSubjectId]);


  const handleSubmit = (e) => {
    e.preventDefault();

    if (!chapterName || !selectedSubjectId || !selectedClassId) {
      alert("Please enter chapter name and select subject");
      return;
    }

    const payload = {
      chapter_name: chapterName,
      subject_id: selectedSubjectId,
      class_id: selectedClassId,
    };

    if (editingChapter) {
      dispatch(updateChapterThunk({ chapterId: editingChapter.chapter_id, payload }));
    } else {
      dispatch(createChapterThunk(payload));
    }

    dispatch(setChapterName(""));
    dispatch(setEditingChapter(null));
  };

  const handleDelete = (chapterId) => {
    if (!window.confirm("Are you sure you want to delete this chapter?")) return;
    dispatch(deleteChapterThunk(chapterId));
  };

  const tableColumns = [
  { header: "#", accessor: "index" },
  { header: "Chapter Name", accessor: "chapter_name" },
  { header: "Actions", accessor: "actions" },
];


  const processChapters = (chapterList) =>
    chapterList.map((chap, index) => ({
      index: index + 1,
      chapter_name: chap.chapter_name,
      actions: (
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => {
              dispatch(setEditingChapter(chap));
              dispatch(setChapterName(chap.chapter_name));
              dispatch(setSelectedSubjectId(chap.subject_id));
            }}
            className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500"
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(chap.chapter_id)}
            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
          >
            Delete
          </button>
        </div>
      ),
    }));


  return (
    <div className="container-fluid w-100 mx-auto py-2 font-inter">
      <div className="row mx-auto w-100">
        <div className="col-md-12 w-100">
          <h3 className="text-3xl font-bold text-center mb-6 text-gray-800">Chapter</h3>

          <form
            onSubmit={handleSubmit}
            className="bg-white mb-8 max-w-4xl mx-auto p-4 rounded shadow"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Class Dropdown */}
              <div className="col-md-6">
                <div className="row mb-2">
                  <label className="block text-sm col-sm-4 font-medium text-gray-700 mb-1">
                    Select Class:
                  </label>
                  <div className="col-sm-8">
                    <select
                      value={selectedClassId}
                      onChange={(e) => {
                        dispatch(setSelectedClassId(e.target.value));
                        dispatch(setSelectedSubjectId(""));
                      }}
                      className="w-full p-2 form-control border border-gray-300 rounded-md"
                    >
                      <option value="">-- Select Class --</option>
                      {classes.map((cls) => (
                        <option key={cls.class_id} value={cls.class_id}>
                          {cls.class_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Subject Dropdown */}
              <div className="col-md-6">
                <div className="row mb-2">
                  <label className="block text-sm col-sm-4 font-medium text-gray-700 mb-1">
                    Select Subject:
                  </label>
                  <div className="col-sm-8">
                    <select
                      value={selectedSubjectId}
                      onChange={(e) => dispatch(setSelectedSubjectId(e.target.value))}
                      className="w-full p-2 form-control border border-gray-300 rounded-md"
                      disabled={!selectedClassId || loadingSubjects}
                    >
                      <option value="">-- Select Subject --</option>
                      {subjects.map((sub) => (
                        <option key={sub.subject_id} value={sub.subject_id}>
                          {sub.subject_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Chapter Input */}
              <div className="col-md-12">
                <div className="row mb-2">
                  <label className="block text-sm col-sm-2 font-medium text-gray-700 mb-1">
                    Chapter Name:
                  </label>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      value={chapterName}
                      onChange={(e) => dispatch(setChapterName(e.target.value))}
                      placeholder="Enter Chapter Name"
                      className="w-full p-2 form-control border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="col-sm-2">
                    <button
                      type="submit"
                      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
                    >
                      {editingChapter ? "Update" : "Add"} Chapter
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
                        
      {/* Chapter List */}
      <div className="row mt-4 mx-auto w-100">
    <div className="col-md-12 w-100">
      <div className="bg-white max-w-4xl mx-auto p-4 rounded shadow">
        <h4 className="text-xl font-semibold mb-4 text-gray-800">Chapter List</h4>
        <TableComponent
          tableColumns={tableColumns}
          tableData={processChapters(chapters)}
        />
      </div>
    </div>
  </div>


    </div>
  );
};

export default ChapterManager;
